import { useMemo } from 'react';

import { TopicCategory, VisitDocumentTemplateMetadata } from '@dreryk/egabinet-visit-frontend-api';

import { EmptyStatePlaceholder, rowIndexKeyExtractor, Table } from '@dreryk/react-components';

import { documentTemplateActionTranslationKeys } from '../../../../../translations';
import { useDocumentTemplateDraftQuery } from '../../DocumentTemplateAction.hooks';

import {
   DocumentTemplateColumnsRenderOptions,
   useDocumentTemplateColumns,
   useDocumentTemplateDataConfiguration,
} from './DocumentTemplateTable.hooks';
import { StyledSearchDocumentTemplatesTable } from './DocumentTemplateTable.styled';

type DocumentTemplateTableProps = {
   readonly visitId: number;
   readonly searchText: string | null;
   readonly topicCategory: TopicCategory | null;
   readonly favorite: boolean;
   readonly onDocumentTemplateOpen: VoidFunction;
};

export const DocumentTemplateTable = (props: DocumentTemplateTableProps): JSX.Element => {
   const { priorityTemplateIds, priorityTemplateTypes } = useDocumentTemplateDraftQuery(props.visitId);

   const isSearching = Boolean(props.searchText);

   const dataConfiguration = useDocumentTemplateDataConfiguration({
      visitId: props.visitId,
      searchText: props.searchText,
      topicCategory: props.topicCategory,
      favorite: props.favorite,
      priorityTemplateIds: priorityTemplateIds,
      priorityTemplateTypes: priorityTemplateTypes,
   });
   const columns = useDocumentTemplateColumns({
      visitId: props.visitId,
      onDocumentTemplateOpen: props.onDocumentTemplateOpen,
      onFavoriteChange: dataConfiguration.refetch,
      priorityTemplateIds: priorityTemplateIds,
      priorityTemplateTypes: priorityTemplateTypes,
   });

   const cellRenderOptions = useMemo<DocumentTemplateColumnsRenderOptions>(
      () => ({
         currentPageData: dataConfiguration.dataPart?.data ?? [],
         showLetterSeparators: isSearching,
      }),
      [dataConfiguration.dataPart?.data, isSearching]
   );

   const TableComponent = isSearching ? StyledSearchDocumentTemplatesTable : Table;

   return (
      <TableComponent<VisitDocumentTemplateMetadata, DocumentTemplateColumnsRenderOptions>
         testId={'documentTemplates'}
         dataConfiguration={dataConfiguration}
         keyExtractor={rowIndexKeyExtractor}
         columns={columns}
         cellRenderOptions={cellRenderOptions}
         placeholder={
            <EmptyStatePlaceholder
               heading={documentTemplateActionTranslationKeys.form.table.placeholder}
               testId={'noDocumentTemplates'}
            />
         }
      />
   );
};
